import React from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { parseVerses } from '../utils/bibleTextFormatter';
import styles from './styles.module.css';
import { format } from 'date-fns';  // Add date-fns for date formatting

interface Reading {
  reference: string;
  text: string;
}

interface ReadingModalProps {
  show: boolean;
  onHide: () => void;
  readings: Reading[];
  isLoading: boolean;
  liturgyName: string;
  liturgyDate: string;  // This should be in a format we can convert
}

const ReadingModal: React.FC<ReadingModalProps> = ({ show, onHide, readings, isLoading, liturgyName, liturgyDate }) => {
  // Format the liturgy date into a readable format
  const formattedLiturgyDate = format(new Date(liturgyDate), 'MMMM d, yyyy'); // e.g., January 23, 2024

  const renderPassage = (reading: Reading, index: number) => {
    const verses = parseVerses(reading.text);
    
    return (
      <div key={reading.reference} className={styles.passageContainer}>
        {/* Reference (in bold and larger font) */}
        <div className={styles.passageReference}>{reading.reference}</div>
        <div className={styles.passageDivider} />
        <div className={styles.passageText}>
          {verses.map((verse, idx) => (
            <div key={`${reading.reference}-${verse.number}-${idx}`} className={styles.verse}>
              {verse.number && (
                <span className={styles.verseNumber}>{verse.number}.</span>
              )}
              <span className={styles.verseText}>{verse.text}</span>
            </div>
          ))}
        </div>
        {/* Line separator between readings */}
        {index < readings.length - 1 && (
          <hr className={styles.separator} />
        )}
      </div>
    );
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" className={styles.readingModal}>
      <Modal.Header closeButton>
        <Modal.Title>
          <div className={styles.liturgyHeader}>
            {/* Display Proper Name */}
            <h4>{liturgyName}</h4>
            {/* Display formatted liturgy date */}
            <p>{formattedLiturgyDate}</p>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className={styles.modalBody}>
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <Spinner animation="border" />
          </div>
        ) : readings.length > 0 ? (
          readings.map((reading, index) => renderPassage(reading, index))
        ) : (
          <div className={styles.errorMessage}>No readings available</div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ReadingModal;
